import { createApiClient, SESSION_SUPERSEDED_ERROR_CODE } from '@imobdesk/api-client';
import type { ResponseParser } from '@imobdesk/api-client';
import { refreshTokenResponseSchema } from '@/api/auth.schema';
import { API_BASE_URL, DEV_API_DIRECT_BASE_URL } from '@/config/api';
import { recordApiFailureBreadcrumb } from '@/lib/api-failure-breadcrumb';
import { parseWith } from '@/lib/api-parse';
import { goToLoginApp } from '@/lib/login-app-url';
import { clearAccessToken, getAccessToken, saveAccessToken } from '@/lib/token-storage';

const REFRESH_ENDPOINT = '/auth/refresh-token';

let loginRedirectTriggered = false;

function redirectToLoginAfterSessionExpired(errorCode?: string): void {
  if (loginRedirectTriggered || typeof window === 'undefined') return;
  loginRedirectTriggered = true;
  clearAccessToken();
  goToLoginApp(
    errorCode === SESSION_SUPERSEDED_ERROR_CODE
      ? { sessionEnded: 'superseded' }
      : undefined,
  );
}

async function refreshAccessTokenFromCookie(): Promise<void> {
  const refreshUrl = `${API_BASE_URL.replace(/\/$/, '')}${REFRESH_ENDPOINT}`;
  const response = await fetch(refreshUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'ngrok-skip-browser-warning': 'true',
      'X-Imobdesk-Entry-App': 'admindesk',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Falha ao renovar sessão');
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error('Resposta inválida ao renovar sessão');
  }

  const raw = await response.json();
  const data = parseWith(refreshTokenResponseSchema, raw);
  if (!data.accessToken) {
    throw new Error('Token de acesso ausente na renovação');
  }

  saveAccessToken(data.accessToken);
}

const client = createApiClient({
  baseUrl: API_BASE_URL,
  entryApp: 'admindesk',
  tokenStorage: {
    getAccessToken,
    clearTokens: clearAccessToken,
    saveAccessToken,
  },
  refreshTokenEndpoint: REFRESH_ENDPOINT,
  refreshToken: refreshAccessTokenFromCookie,
  devDirectBaseUrl: DEV_API_DIRECT_BASE_URL,
  extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
  onSessionInvalidated: async (ctx) => {
    redirectToLoginAfterSessionExpired(ctx.errorCode);
  },
});

class ApiService {
  private sanitizeErrorMessage(message?: string): string {
    const safeMessages: Record<string, string> = {
      'Email ou senha inválidos': 'Email ou senha inválidos',
      'Email já cadastrado': 'Email já cadastrado',
      'Token inválido': 'Sessão inválida. Faça login novamente.',
      'Token expirado': 'Sessão expirada. Faça login novamente.',
      'Acesso negado': 'Você não tem permissão para acessar este recurso.',
      'Registro não encontrado': 'Registro não encontrado.',
      'Dados inválidos': 'Dados inválidos. Verifique as informações.',
      'Plano não encontrado': 'Plano não encontrado.',
      'Usuário não encontrado': 'Usuário não encontrado.',
    };

    if (!message) {
      return 'Ocorreu um erro. Tente novamente.';
    }

    if (safeMessages[message]) {
      return safeMessages[message];
    }

    const partialMatches = [
      { pattern: /já existe/i, safe: true },
      { pattern: /não encontrad/i, safe: true },
      { pattern: /inválid/i, safe: true },
      { pattern: /obrigatóri/i, safe: true },
      { pattern: /não permitid/i, safe: true },
      { pattern: /em uso/i, safe: true },
      { pattern: /não é possível/i, safe: true },
      { pattern: /associad/i, safe: true },
    ];

    for (const match of partialMatches) {
      if (match.pattern.test(message)) {
        return message;
      }
    }

    return 'Ocorreu um erro ao processar sua solicitação.';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = API_BASE_URL
      ? `${API_BASE_URL.replace(/\/$/, '')}${endpoint}`
      : endpoint;

    let response: Response;
    try {
      response = await client.apiClient(endpoint, options);
    } catch (networkError) {
      const msg =
        networkError instanceof Error ? networkError.message : String(networkError);
      recordApiFailureBreadcrumb({
        kind: 'network',
        url,
        detail: `${msg} — Verifique: (1) API Nest rodando na porta de VITE_IMOBDESK_API_URL; (2) terminal do admindesk por [admindesk vite proxy]; (3) abrir http://localhost:8080/api/v2/health no navegador.`,
      });
      console.error(
        '[Assinar API] fetch falhou (rede, CORS ou SSL). URL:',
        url,
        networkError,
      );
      throw new Error('Erro de conexão: Não foi possível conectar ao servidor.');
    }

    const responseText = await response.text();

    if (responseText.startsWith('<!DOCTYPE') || responseText.startsWith('<html')) {
      if (import.meta.env.DEV) {
        console.error('Resposta HTML recebida em vez de JSON:', responseText.substring(0, 500));
      }
      throw new Error('Servidor retornou uma resposta inesperada. Tente novamente.');
    }

    let data: T;
    try {
      data = (responseText ? JSON.parse(responseText) : {}) as T;
    } catch {
      if (import.meta.env.DEV) {
        console.error('Erro ao fazer parse do JSON:', responseText.substring(0, 500));
      }
      throw new Error('Resposta inválida do servidor.');
    }

    if (!response.ok) {
      if (response.status === 401) {
        const errorCode =
          typeof data === 'object' && data !== null && 'code' in data
            ? String((data as { code?: unknown }).code ?? '')
            : undefined;
        const msg401 =
          (data as Record<string, unknown>)?.message != null
            ? String((data as Record<string, unknown>).message)
            : '401 sem mensagem';
        recordApiFailureBreadcrumb({
          kind: 'http401',
          url,
          status: 401,
          detail: msg401,
        });
        redirectToLoginAfterSessionExpired(errorCode);
        throw new Error('Sessão expirada. Redirecionando para o login...');
      }

      const backendMessage = (data as Record<string, unknown>)?.message as string;
      const errorMessage = backendMessage || JSON.stringify(data, null, 2);
      if (import.meta.env.DEV) {
        console.error('Erro HTTP:', response.status, data);
      }
      throw new Error(this.sanitizeErrorMessage(errorMessage));
    }

    return data;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async getParsed<T>(endpoint: string, schema: ResponseParser<T>): Promise<T> {
    const data = await this.request<unknown>(endpoint, { method: 'GET' });
    return parseWith(schema, data);
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async postParsed<T>(
    endpoint: string,
    body: unknown,
    schema: ResponseParser<T>,
  ): Promise<T> {
    const data = await this.request<unknown>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return parseWith(schema, data);
  }

  async putParsed<T>(
    endpoint: string,
    body: unknown,
    schema: ResponseParser<T>,
  ): Promise<T> {
    const data = await this.request<unknown>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return parseWith(schema, data);
  }

  async patchParsed<T>(
    endpoint: string,
    body: unknown,
    schema: ResponseParser<T>,
  ): Promise<T> {
    const data = await this.request<unknown>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return parseWith(schema, data);
  }

  async deleteParsed<T>(
    endpoint: string,
    schema: ResponseParser<T>,
    body?: unknown,
  ): Promise<T> {
    const data = await this.request<unknown>(endpoint, {
      method: 'DELETE',
      body: JSON.stringify(body ?? {}),
    });
    return parseWith(schema, data);
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', body: JSON.stringify(data ?? {}) });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiService();
